import { pgTable, uuid, text, timestamp, jsonb, integer, index, unique, boolean, } from "drizzle-orm/pg-core";
import { relations } from "drizzle-orm";
import { organisations } from "./organisations";
import { users } from "./users";
import { calendarEvents } from "./gcal";
export const conversations = pgTable("conversations", {
    id: uuid("id").primaryKey().defaultRandom(),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    userId: uuid("user_id")
        .notNull()
        .references(() => users.id, { onDelete: "cascade" }),
    calendarEventId: uuid("calendar_event_id").references(() => calendarEvents.id, {
        onDelete: "set null",
    }),
    title: text("title"),
    metadata: jsonb("metadata").$type().default({}),
    summary: jsonb("summary").$type(),
    pendingPlan: jsonb("pending_plan").$type(),
    contextSummary: text("context_summary"),
    contextSummaryAt: timestamp("context_summary_at", { withTimezone: true }),
    candorMode: boolean("candor_mode").notNull().default(false),
    candorModeActivatedAt: timestamp("candor_mode_activated_at", { withTimezone: true }),
    availableToOrg: boolean("available_to_org").notNull().default(false),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
    archivedAt: timestamp("archived_at", { withTimezone: true }),
}, table => ({
    companyIdx: index("idx_conversations_company").on(table.companyId, table.createdAt),
    userIdx: index("idx_conversations_user").on(table.userId, table.createdAt),
    calendarEventIdx: index("idx_conversations_calendar_event").on(table.calendarEventId),
}));
export const messages = pgTable("messages", {
    id: uuid("id").primaryKey().defaultRandom(),
    conversationId: uuid("conversation_id")
        .notNull()
        .references(() => conversations.id, { onDelete: "cascade" }),
    role: text("role", { enum: ["user", "assistant", "system", "tool"] }).notNull(),
    content: text("content").notNull(),
    toolName: text("tool_name"), // Only populated for tool role messages
    metadata: jsonb("metadata").$type().default({}),
    // Context & reasoning
    contextUsed: jsonb("context_used").$type(),
    reasoningTrace: jsonb("reasoning_trace").$type(),
    // Status tracking
    status: text("status", {
        enum: ["pending", "streaming", "completed", "failed", "cancelled"],
    })
        .notNull()
        .default("pending"),
    errorMessage: text("error_message"),
    // Timing
    startedAt: timestamp("started_at", { withTimezone: true }),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    // Performance metrics
    tokensUsed: integer("tokens_used"),
    processingDurationMs: integer("processing_duration_ms"),
}, table => ({
    conversationIdx: index("idx_messages_conversation").on(table.conversationId, table.createdAt),
}));
export const messageChunks = pgTable("message_chunks", {
    id: uuid("id").primaryKey().defaultRandom(),
    messageId: uuid("message_id")
        .notNull()
        .references(() => messages.id, { onDelete: "cascade" }),
    sequenceNumber: integer("sequence_number").notNull(),
    content: text("content").notNull(),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
}, table => ({
    messageIdx: index("idx_chunks_message").on(table.messageId, table.sequenceNumber),
    uniqueMessageSequence: unique("unique_message_sequence").on(table.messageId, table.sequenceNumber),
}));
export const conversationParticipants = pgTable("conversation_participants", {
    id: uuid("id").primaryKey().defaultRandom(),
    conversationId: uuid("conversation_id")
        .notNull()
        .references(() => conversations.id, { onDelete: "cascade" }),
    userId: uuid("user_id")
        .notNull()
        .references(() => users.id, { onDelete: "cascade" }),
    role: text("role", { enum: ["owner", "member", "viewer"] })
        .notNull()
        .default("member"),
    joinedAt: timestamp("joined_at", { withTimezone: true }).notNull().defaultNow(),
    lastReadAt: timestamp("last_read_at", { withTimezone: true }),
}, table => ({
    conversationIdx: index("idx_participants_conversation").on(table.conversationId),
    userIdx: index("idx_participants_user").on(table.userId),
    uniqueConversationUser: unique("unique_conversation_user").on(table.conversationId, table.userId),
}));
// Relations
export const conversationsRelations = relations(conversations, ({ one, many }) => ({
    company: one(organisations, {
        fields: [conversations.companyId],
        references: [organisations.id],
    }),
    user: one(users, {
        fields: [conversations.userId],
        references: [users.id],
    }),
    messages: many(messages),
    participants: many(conversationParticipants),
}));
export const messagesRelations = relations(messages, ({ one, many }) => ({
    conversation: one(conversations, {
        fields: [messages.conversationId],
        references: [conversations.id],
    }),
    chunks: many(messageChunks),
}));
export const messageChunksRelations = relations(messageChunks, ({ one }) => ({
    message: one(messages, {
        fields: [messageChunks.messageId],
        references: [messages.id],
    }),
}));
export const conversationParticipantsRelations = relations(conversationParticipants, ({ one }) => ({
    conversation: one(conversations, {
        fields: [conversationParticipants.conversationId],
        references: [conversations.id],
    }),
    user: one(users, {
        fields: [conversationParticipants.userId],
        references: [users.id],
    }),
}));
export const conversationSummaryCounters = pgTable("conversation_summary_counters", {
    conversationId: uuid("conversation_id")
        .primaryKey()
        .references(() => conversations.id, { onDelete: "cascade" }),
    companyId: uuid("company_id")
        .notNull()
        .references(() => organisations.id, { onDelete: "cascade" }),
    incrementalCount: integer("incremental_count").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
        .notNull()
        .defaultNow()
        .$onUpdate(() => new Date()),
});
export const conversationSummaryCountersRelations = relations(conversationSummaryCounters, ({ one }) => ({
    conversation: one(conversations, {
        fields: [conversationSummaryCounters.conversationId],
        references: [conversations.id],
    }),
    company: one(organisations, {
        fields: [conversationSummaryCounters.companyId],
        references: [organisations.id],
    }),
}));
